import React, { useState, useEffect } from 'react';
import {
  Page, PageHeader, PageBody,
  Card, Button, Spinner,
  DataTable, DataTableActions
} from '../../components/ui';
import { AXIOS_INSTANCE } from '../../services/api-client';
import {
  CheckIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

function sourceLabel(row: any): string | null {
  const metadata = row.source_metadata;
  if (!metadata || typeof metadata !== 'object') return null;
  const workspace = typeof metadata.workspace_name === 'string' ? metadata.workspace_name : null;
  const source = typeof metadata.source_surface === 'string' ? metadata.source_surface.replace(/_/g, ' ') : null;
  if (workspace && source) return `${workspace} · ${source}`;
  return workspace || source;
}

export default function CatalogCorrectionQueuePage() {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<number | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await AXIOS_INSTANCE.get('/api/v1/catalog/feedback/corrections/pending');
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch correction requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (requestId: number) => {
    setProcessingId(requestId);
    try {
      await AXIOS_INSTANCE.post(`/api/v1/catalog/feedback/corrections/${requestId}/approve`, {
        reviewer_notes: 'Approved via Steward Dashboard'
      });
      setRequests(requests.filter(r => r.id !== requestId));
    } catch (error) {
      console.error('Failed to approve request:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: number) => {
    setProcessingId(requestId);
    try {
      await AXIOS_INSTANCE.post(`/api/v1/catalog/feedback/corrections/${requestId}/reject`, {
        reviewer_notes: 'Rejected via Steward Dashboard'
      });
      setRequests(requests.filter(r => r.id !== requestId));
    } catch (error) {
      console.error('Failed to reject request:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const columns = [
    {
      id: 'asset',
      header: 'Asset',
      cell: ({ row }: any) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900 dark:text-white">
            {row.catalog_asset?.display_name || row.catalog_asset?.name}
          </span>
          <span className="text-[10px] text-gray-500 font-mono">
            {row.field_path}
          </span>
        </div>
      )
    },
    {
      id: 'correction',
      header: 'Proposed Correction',
      cell: ({ row }: any) => (
        <div className="flex flex-col max-w-md">
          <div className="text-xs text-red-500 line-through opacity-60 truncate">
            {row.original_value || '(Empty)'}
          </div>
          <div className="text-sm text-green-600 dark:text-green-400 font-medium whitespace-pre-wrap">
            {row.proposed_value}
          </div>
        </div>
      )
    },
    {
      id: 'requester',
      header: 'Requester Info',
      cell: ({ row }: any) => (
        <div className="flex flex-col">
          <span className="text-xs text-gray-700 dark:text-gray-300">User #{row.user_id}</span>
          {row.reason && (
            <div className="flex items-center gap-1 mt-1">
              <ChatBubbleLeftRightIcon className="w-3 h-3 text-gray-400" />
              <span className="text-[10px] text-gray-400 truncate max-w-[150px] italic">
                {row.reason}
              </span>
            </div>
          )}
          {sourceLabel(row) && (
            <span className="mt-1 text-[10px] font-medium text-amber-600 dark:text-amber-400">
              {sourceLabel(row)}
            </span>
          )}
        </div>
      )
    },
    {
      id: 'submitted',
      header: 'Submitted',
      cell: ({ row }: any) => (
        <span className="text-xs text-gray-500">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: any) => (
        <DataTableActions>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 h-8"
              onClick={() => handleApprove(row.id)}
              disabled={processingId === row.id}
            >
              {processingId === row.id ? <Spinner className="h-3 w-3" /> : <CheckIcon className="h-4 w-4" />}
              <span className="ml-1">Approve</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 h-8"
              onClick={() => handleReject(row.id)}
              disabled={processingId === row.id}
            >
              <XMarkIcon className="h-4 w-4" />
              <span className="ml-1">Reject</span>
            </Button>
          </div>
        </DataTableActions>
      )
    }
  ];

  return (
    <Page>
      <PageHeader
        title="Catalog Correction Queue"
        description="Review and approve crowdsourced metadata corrections for the Atlan Catalog."
      >
        <Button onClick={fetchRequests} variant="outline" size="sm">
          Refresh Queue
        </Button>
      </PageHeader>
      <PageBody>
        <Card className="overflow-hidden border-none shadow-sm">
          {loading ? (
            <div className="py-24 text-center">
              <Spinner className="h-10 w-10 mx-auto mb-4 text-brand" />
              <p className="text-gray-500 font-medium">Loading pending requests...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="py-24 text-center">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4 animate-bounce" />
              <p className="text-gray-900 dark:text-white font-bold text-xl">All caught up!</p>
              <p className="text-gray-500 mt-2 max-w-xs mx-auto">
                There are no pending metadata correction requests to review at this time.
              </p>
            </div>
          ) : (
            <DataTable
              data={requests}
              columns={columns}
              rowClassName={() => "hover:bg-gray-50/50 dark:hover:bg-gray-900/20 transition-colors"}
            />
          )}
        </Card>
      </PageBody>
    </Page>
  );
}
